import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import { getAll, getSelectedJurorIds } from '../../state/multiSelect/selectors';
import Modes from '../../state/multiSelect/modes';
import { ActionCreators } from '../../state/multiSelect/actions';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Chip from "@mui/material/Chip";
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import { multiJurorEdit } from '../../db/actions';
import Typography from '@mui/material/Typography';
import RichTextEditor from '../RichTextEditor';
import { useCase, useJurors } from '../../db/hooks';
import Grid from '@mui/material/Grid';
import DemographicsOptions from '../Demographics/DemographicsOptions';
import {isMacOs, isWindows} from "react-device-detect";

const useStyles = makeStyles((theme) => ({
  dialog: {
    width: 600
  },
  jurorNameList: {
    listStyle: "none",
    padding: theme.spacing(0.5),
    display: "flex",
    flexWrap: "wrap"
  },
  jurorNameChip: {
    padding: theme.spacing(0.1),
    marginRight: theme.spacing(0.2),
    marginBottom: theme.spacing(0.2)
  },
  actions: {
    '&.MuiDialogActions-root': {
      padding: theme.spacing(2, 3),
    },
  },
  commentsLabel: {
    color: '#808080',
    marginLeft: '5px',
    userSelect: 'none',
  },
  submitInstructions: {
    marginRight: 'auto',
    color: 'gray',
    fontSize: 14,
    display: "flex",
    flexDirection: "column",
    gap: "0.5em"
  },
  code: {
    backgroundColor: "#333",
    color: "white",
    borderRadius: "4px",
    padding: "3px 5px"
  }
}));

export default ({caseId}) => {
  const dispatch = useDispatch();
  const multiSelect = useSelector(getAll);
  const selectedJurorIds = useSelector(getSelectedJurorIds);
  const { jurors: allJurors } = useJurors(caseId);
  const { caseData: caseInfo } = useCase(caseId);

  const matchingJurorNames = React.useMemo(() => {
    const idSet = new Set(selectedJurorIds);
    return allJurors
      .filter(j => idSet.has(j._id))
      .map(j => j.nameAndId || j._id);
  }, [allJurors, selectedJurorIds]);

  const [demographicsOptions, setDemographicsOptions] = React.useState([]);

  //console.log('Demographics options: ', demographicsOptions, tmpDemographicsOptions);
  const [demographics, setDemographics] = React.useState([]);
  const [comments, setComments] = React.useState({
    text: "",
    styles: []
  });

  const classes = useStyles();
  const editorRef = React.useRef(null);

  function handleClose() {
    dispatch(ActionCreators.setMode(Modes.ON));
  }

  async function handleSave() {
    const content = editorRef.current.getContent();

    await multiJurorEdit(caseId, multiSelect.selectedJurorIds, {
      demographics,
      commentText: content.comments,
      commentsStyles: content.richTextStyles,
    });
    
    // Reset state and close modal
    setDemographics([]);
    setComments({
      text: "",
      styles: []
    });
    dispatch(ActionCreators.setMode(Modes.OFF));
  }

  return (
    <Dialog open={multiSelect.mode === Modes.COMPOSING_COMMENT}
            onClose={handleClose}
            aria-labelledby="multi-edit-dialog"
            width="lg">
      <DialogContent className={classes.dialog}>
        <DialogContentText>Multi-Juror Edit</DialogContentText>
        <ul className={classes.jurorNameList}>
          {matchingJurorNames.map((jurorName, index) => {
            return (
              <li key={jurorName + "-" + index}>
                <Chip icon={null} label={jurorName} className={classes.jurorNameChip}/>
              </li>
            )
          })}
        </ul>

        {(caseInfo && caseInfo.demographicCategories && caseInfo.demographicCategories.length > 0) &&
        <Grid size={12}>
          <DemographicsOptions caseId={caseId}
                               options={demographicsOptions}
                               demographics={demographics}
                               setOptions={setDemographicsOptions}
                               setDemographics={setDemographics} />
        </Grid>
        }

        <RichTextEditor label={<Typography variant={"caption"} className={classes.commentsLabel}>Comments</Typography>}
                        richTextStyles={comments.styles}
                        text={comments.text}
                        onSave={handleSave}
                        autoFocus
                        ref={editorRef} />
      </DialogContent>
      <DialogActions className={classes.actions}>
        <div className={classes.submitInstructions}>
          {isWindows && <div>Emoji keyboard: <code className={classes.code}>Win</code> + <code className={classes.code}>.</code></div>}
          {isMacOs && <div>Emoji keyboard: <code className={classes.code}>Cmd</code> + <code className={classes.code}>Ctrl</code> + <code className={classes.code}>Space</code></div>}
          <div>Confirm: <code className={classes.code}>Shift</code> + <code className={classes.code}>Enter</code></div>
        </div>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
        >
          Save
        </Button>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
